import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import {
  Box,
  Button,
  Container,
  Grid,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import Cancelled from "assets/illustrations/Cancelled.svg";
import Celebration from "assets/illustrations/Celebration-rafiki.svg";
import Pending from "assets/illustrations/Pedestriancrossing-rafiki.svg";
import Error from "assets/illustrations/Warning-rafiki.svg";

// Shared brand color
const primaryColor = "#f15e22";
const steps = ["Payment received"];

// Mapping statuses to their props
const statusConfig = {
  success: {
    title: "Payment successful",
    description:
      "Thank you for choosing Sentoo. Your custom reports will be generated within two business days.",
    icon: (
      <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "success.main" }} />
    ),
    illustration: Celebration,
    activeStep: 1,
  },
  failed: {
    title: "Payment Failed",
    description:
      "We couldn’t process your payment. Please try again or contact support if this issue persists.",
    icon: <CancelOutlinedIcon sx={{ fontSize: 40, color: "error.main" }} />,
    illustration: Error,
    activeStep: 1,
    errorStep: 0,
    errorLabel: "Failed Payment",
  },
  cancelled: {
    title: "Payment Cancelled",
    description: "Your payment has been cancelled. Please try again if needed.",
    icon: (
      <ReportProblemOutlinedIcon sx={{ fontSize: 40, color: "warning.main" }} />
    ),
    illustration: Cancelled,
    activeStep: 1,
    errorStep: 0,
    errorLabel: "Cancelled Payment",
  },
  pending: {
    title: "Payment Pending",
    description: "Your payment is pending. Please wait until it is confirmed.",
    icon: <HourglassEmptyIcon sx={{ fontSize: 40, color: "info.main" }} />,
    illustration: Pending,
    activeStep: 1,
  },
  rejected: {
    title: "Payment Rejected",
    description:
      "Your payment was rejected. Please contact support for assistance.",
    icon: <ErrorOutlineIcon sx={{ fontSize: 40, color: "error.main" }} />,
    illustration: Error,
    activeStep: 1,
    errorStep: 0,
    errorLabel: "Rejected Payment",
  },
};

function StatusView({ status }) {
  const navigate = useNavigate();
  const config = statusConfig[status] || statusConfig.failed;

  return (
    <Container maxWidth="lg" sx={{ py: 10 }}>
      <Grid container spacing={6} alignItems="center" justifyContent="center">
        {/* Left Section - Text */}
        <Grid item xs={12} md={6}>
          <Stack spacing={3}>
            <Stack direction="row" alignItems="center" spacing={1}>
              {config.icon}
              <Typography variant="h4" fontWeight="bold">
                {config.title}
              </Typography>
            </Stack>

            <Typography variant="body1" color="text.secondary">
              {config.description}
            </Typography>

            <Stepper activeStep={config.activeStep}>
              {steps.map((label, index) => {
                const labelProps = {};
                if (config.errorStep === index) {
                  labelProps.optional = (
                    <Typography variant="caption" color="error">
                      {config.errorLabel}
                    </Typography>
                  );
                  labelProps.error = true;
                }
                return (
                  <Step key={label}>
                    <StepLabel {...labelProps}>
                      {config.errorStep !== 0 && label}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>

            <Button
              variant="contained"
              onClick={() => navigate("/")}
              sx={{
                backgroundColor: primaryColor,
                width: "180px",
                "&:hover": { backgroundColor: "#d94f1a" },
                mt: "52px !important",
              }}
              size="large"
            >
              Back Home
            </Button>
          </Stack>
        </Grid>

        {/* Right Section - Illustration */}
        <Grid item xs={12} md={6}>
          <Box
            component="img"
            src={config.illustration}
            alt={`${status} Illustration`}
            sx={{
              width: "100%",
              maxWidth: 480,
              display: "block",
              margin: "0 auto",
            }}
          />
        </Grid>
      </Grid>
    </Container>
  );
}

function SentooPaymentStatus() {
  const { variable } = useParams();
  return (
    <Box sx={{ backgroundColor: "#f8f8f8", minHeight: "100vh" }}>
      <StatusView status={variable} />
    </Box>
  );
}

export default SentooPaymentStatus;
